import React, { forwardRef, useState } from 'react';
import Inputmask from 'inputmask';
import PropTypes from 'prop-types';
import { ARROW_LEFT_KEY, ARROW_RIGHT_KEY } from '../../Utils/constant';
import useIsMobile from '../../Utils/customHooks/useIsMobile';
import { DEFAULT_TIME_12_HOUR_FORMAT, DEFAULT_TIME_24_HOUR_FORMAT, timeFormat } from '../utils/constant';
import { formattedTime, getInputTimeMaskOptions, getInputTimeMaskOptions24, getSecondsFromTime, getSecondsFromTime24 } from '../utils/helper';
import styles from '@dtsl/css-timepicker/dist/index.module.css';
import { Clock } from '@dtsl/icons/dist/icons/react';
import { Button } from '@dtsl/react/dist/components/Button';
import { Input } from '@dtsl/react/dist/components/Input';
const TimepickerInput = /*#__PURE__*/forwardRef(function TimepickerInput(_ref, ref) {
  let {
    className = '',
    size,
    is24HourFormat = false,
    triggerRef,
    onChange,
    time,
    setIsFloatingPanelVisible,
    isFloatingPanelVisible,
    onTriggerClick
  } = _ref;
  const [cursorPosition, setCursorPosition] = useState({
    current: 0,
    start: 0,
    end: 0,
    direction: 'forward'
  });
  const {
    isMobile
  } = useIsMobile(640);
  if (ref.current) {
    Inputmask({
      ...(is24HourFormat ? getInputTimeMaskOptions24(DEFAULT_TIME_24_HOUR_FORMAT, DEFAULT_TIME_24_HOUR_FORMAT) : getInputTimeMaskOptions(timeFormat, DEFAULT_TIME_12_HOUR_FORMAT))
    }).mask(ref.current);
  }
  const onInputChange = e => {
    let convertedTime = is24HourFormat ? getSecondsFromTime24(e.target.value) : getSecondsFromTime(e.target.value);
    onChange(convertedTime);
    if (cursorPosition.current < cursorPosition.end - 1) {
      setCursorPosition(prev => ({
        ...prev,
        current: prev.current + 1
      }));
    } else {
      setCursorPosition({
        start: cursorPosition.start + 3,
        end: cursorPosition.end + 3,
        current: cursorPosition.start + 3
      });
    }
  };
  const onFocus = () => {
    ref.current.setSelectionRange(0, 2);
  };
  const onkeyPress = e => {
    const keyPress = e.key;
    if (keyPress === ARROW_LEFT_KEY) {
      if (cursorPosition.end > 2) {
        setCursorPosition({
          start: cursorPosition.start - 3,
          end: cursorPosition.end - 3,
          current: cursorPosition.start - 3,
          direction: 'backward'
        });
      }
    }
    if (keyPress === ARROW_RIGHT_KEY) {
      if (cursorPosition.end < 6) {
        setCursorPosition({
          start: cursorPosition.start + 3,
          end: cursorPosition.end + 3,
          current: cursorPosition.start + 3,
          direction: 'forward'
        });
      }
    }
  };
  const handleCursorPosition = () => {
    const input = ref.current;
    const currentCursorPosition = input.selectionStart;
    let cursorData = {
      start: 0,
      end: 2,
      current: 0,
      direction: 'forward'
    };
    if (currentCursorPosition >= 3 && currentCursorPosition <= 5) {
      cursorData = {
        start: 3,
        end: 5,
        current: 3,
        direction: 'forward'
      };
    } else if (!is24HourFormat && currentCursorPosition >= 6 && currentCursorPosition <= 8) {
      cursorData = {
        start: 6,
        end: 8,
        current: 6,
        direction: 'forward'
      };
    }
    setCursorPosition(cursorData);
  };
  const onInputClick = () => {
    if (isMobile) {
      setIsFloatingPanelVisible(true);
      return;
    }
    handleCursorPosition();
  };
  const onTimepickerActionClick = e => {
    setIsFloatingPanelVisible(!isFloatingPanelVisible);
    onTriggerClick(!isFloatingPanelVisible, e);
  };
  React.useLayoutEffect(() => {
    if (ref.current) {
      const input = ref.current;
      setTimeout(() => {
        const {
          start,
          end,
          current,
          direction
        } = cursorPosition;
        input.setSelectionRange(start, end, direction);
        input.selectionStart = current;
      }, 5);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cursorPosition]);
  return /*#__PURE__*/React.createElement(Input, {
    type: 'text',
    id: 'timepicker_input_id',
    dataTestId: "timepicker_input_testid",
    datatest: 'timepicker_input_datatest',
    className: `${styles['sib-timepicker']} ${className}`,
    size: size,
    ref: ref,
    onChange: onInputChange,
    onFocus: onFocus,
    value: formattedTime(time, is24HourFormat),
    onKeyDown: onkeyPress,
    onClick: onInputClick,
    placeholder: !is24HourFormat ? DEFAULT_TIME_12_HOUR_FORMAT : DEFAULT_TIME_24_HOUR_FORMAT,
    actions: [() => /*#__PURE__*/React.createElement(Button, {
      iconAlone: true,
      icon: /*#__PURE__*/React.createElement(Clock, {
        color: "#000"
      }),
      variant: "tertiary",
      size: "small",
      ariaLabel: "Bold",
      datatest: "timepicker-trigger-id",
      dataTestId: "timepicker-trigger-id",
      id: "timepicker-trigger-id",
      role: "button",
      onClick: onTimepickerActionClick,
      ref: triggerRef
    })]
  });
});
TimepickerInput.propTypes = {
  size: PropTypes.string,
  time: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  is24HourFormat: PropTypes.bool,
  className: PropTypes.string,
  onTriggerClick: PropTypes.func,
  triggerRef: PropTypes.node.isRequired,
  setIsFloatingPanelVisible: PropTypes.func.isRequired,
  isFloatingPanelVisible: PropTypes.bool.isRequired
};
export default TimepickerInput;